import React, { useState, useEffect } from 'react';
import { Box, Button, Snackbar } from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { useSession } from 'next-auth/react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import { useTheme } from '@mui/material/styles';
import { Theme as MuiTheme } from '@mui/material/styles';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';

// local imports
import PleaseWait from 'components/common/PleaseWait';
import { logClientException } from 'appinsights/clientAppInsights';
import { isAdmin } from 'services/roleUtils';
import { BadRequestError } from 'services/apiWrapper';
import NotAuthorized from 'components/common/NotAuthorized';
import useLoadingOrSigningOut from '../../../../services/hooks/useLoadingOrSigningOut';
import { SecureFileRecord } from 'types/SecureFileTypes';
import { logServerException } from 'appinsights/clientAppInsights';

import {
  allowedExtensions,
  canUploadFile,
  maxSecureFileSize,
} from 'services/secureFileUtils';

import { deleteSecureFile, useSecureFilesList } from './SecureFileHooks';
import { useMutation } from '@tanstack/react-query';
import ConfirmDeleteDialog from 'components/common/ConfirmDeleteDialog';
import { AgilityPageProps } from 'types/AgilityTypes';
import { CategoryTagList } from 'types/SecureFileTypes';
import SecureFileForm from './SecureFileForm';

export type CustomData = {
  categoryTagList: CategoryTagList;
  categoryTagDef: { [category: string]: boolean };
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  },
);

const defaultErrorMessage =
  'An unexpected error has occurred. Please try again.';

const AdminSecureFileList = ({ customData }: AgilityPageProps<CustomData>) => {
  const theme = useTheme() as MuiTheme;
  const { data: session, status } = useSession();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [fileToEdit, setFileToEdit] = useState<SecureFileRecord | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  const [isSecureFileFormOpen, setIsSecureFileFormOpen] =
    useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const {
    data: fileList,
    error: fileListError,
    isFetching: isFetchingFileList,
    refetch: refetchFileList,
  } = useSecureFilesList(!!session && !!isAdmin(session));

  const deleteFileMutation = useMutation({
    mutationFn: deleteSecureFile,
    onSuccess: () => {
      refetchFileList();
      enqueueSnackbar('File deleted successfully', {
        variant: 'success',
        autoHideDuration: 4000,
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
    },
    onError: (error) => {
      logClientException(error);
      enqueueSnackbar(`Failed to delete file`, {
        variant: 'error',
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
    },
  });

  useEffect(() => {
    if (fileListError) {
      setErrorMessage(fileListError.message);
      logClientException(fileListError);
    }
  }, [fileListError]);

  const handleAddFile = async (data: {
    friendlyName: string;
    tags: { [category: string]: string[] };
    file: File | null;
    successCallback: () => void;
  }) => {
    const isAdd = !fileToEdit;

    const uploadFile = data.file;
    if (!uploadFile && isAdd) return;

    try {
      if (isAdd && uploadFile) {
        if (!canUploadFile(uploadFile.name, uploadFile.type)) {
          throw new BadRequestError(
            `Only files of type ${allowedExtensions.join(', ')} are allowed.`,
          );
        }
        if (uploadFile.size > maxSecureFileSize) {
          throw new BadRequestError(
            `File size exceeds the maximum limit of ${maxSecureFileSize / 1024 / 1024} MB.`,
          );
        }
      }
      setUploadingFile(true);
      const formData = new FormData();
      if (isAdd && uploadFile) {
        formData.append('file', uploadFile);
      }
      formData.append('friendly_file_name', data.friendlyName);
      formData.append('tags', JSON.stringify(data.tags));
      if (!isAdd && fileToEdit) {
        formData.append('file_id', fileToEdit.id);
      }

      const res = await fetch('/api/files/upload', {
        method: isAdd ? 'POST' : 'PUT',
        body: formData,
        mode: 'cors',
      });
      if (res.ok) {
        if (res.status === 200) {
          const _resData = await res.json();
          refetchFileList();
          enqueueSnackbar(
            isAdd && uploadFile
              ? `File "${uploadFile.name}" uploaded successfully`
              : `File "${fileToEdit?.file_name}" updated successfully`,
            {
              variant: 'success',
              autoHideDuration: 4000,
              action: (key) => (
                <button onClick={() => closeSnackbar(key)}>Dismiss</button>
              ),
            },
          );

          if (data.successCallback) {
            data.successCallback();
          }
          setFileToEdit(null);
          refetchFileList();

          return;
        }
      }
      if (res.status === 400) {
        throw new BadRequestError(await res.text());
      }
      let err = new Error(
        `API Call failed. Status ${res.status} ${res.statusText}`,
      );
      throw err;
    } catch (error) {
      if (error instanceof BadRequestError) {
        setErrorMessage(error.formatMessage());
      } else {
        setErrorMessage(defaultErrorMessage);
      }
      console.error(error);
      logClientException(error);
    } finally {
      setUploadingFile(false);
      setIsSecureFileFormOpen(false);
    }
  };

  const isWaiting = useLoadingOrSigningOut(status);

  if (isWaiting) {
    return <PleaseWait isLoading={true} />;
  }
  if (!isAdmin(session)) {
    return <NotAuthorized />;
  }

  const handleAlertClose = (
    _event: Event | React.SyntheticEvent<any, Event>,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  async function handleCopyFile(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(window.location.origin + url);
      enqueueSnackbar('Download link copied to clipboard', {
        variant: 'success',
        autoHideDuration: 3000,
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
    } catch (error) {
      setErrorMessage('Failed to copy link to clipboard.');
      logClientException(error);
    }
  }
  async function handleDeleteFile(id: string): Promise<void> {
    if (!id || id.length === 0) return;
    try {
      deleteFileMutation.mutate(id);
    } catch (error) {
      enqueueSnackbar(`Failed to Delete File`, {
        variant: 'error',
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
      logClientException(error);
    }
  }

  const getSortedFiles = () => {
    if (!fileList) return [];
    if (!sortConfig) return fileList;
    const sorted = [...fileList].sort((a, b) => {
      const { key, direction } = sortConfig;
      let aValue = a[key as keyof SecureFileRecord];
      let bValue = b[key as keyof SecureFileRecord];

      // Handle category columns (tags)
      if (Object.keys(customData.categoryTagList).includes(key)) {
        aValue = a.tags?.[key]?.join(', ') || '';
        bValue = b.tags?.[key]?.join(', ') || '';
      }

      // Handle size as number
      if (key === 'size') {
        aValue = aValue || 0;
        bValue = bValue || 0;
        return direction === 'asc'
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }

      // Handle updated_at as date
      if (key === 'updated_at') {
        const aDate = aValue ? new Date(aValue as string).getTime() : 0;
        const bDate = bValue ? new Date(bValue as string).getTime() : 0;
        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Default string comparison
      const aStr = (aValue || '').toString().toLowerCase();
      const bStr = (bValue || '').toString().toLowerCase();
      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedFiles = getSortedFiles();

  return (
    <React.Fragment>
      <PleaseWait
        isLoading={
          uploadingFile || isFetchingFileList || deleteFileMutation.isPending
        }
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleAlertClose}
      >
        <Alert
          onClose={handleAlertClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginRight: '3rem',
        }}
      >
        <Box>
          <Button
            component="label"
            title="Upload a file"
            variant="outlined"
            startIcon={<LibraryAddIcon />}
            color="primary"
            size="small"
            style={{ borderRadius: '30px' }}
            onClick={() => setIsSecureFileFormOpen(true)}
          >
            Add File
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'block',
          marginLeft: '15px',
          marginRight: '15px',
          marginBottom: '50px',
        }}
      >
        <div className="overflow-x-auto bg-white mt-4 mb-4">
          <table className="min-w-full">
            <thead className="">
              <tr>
                {[
                  { key: 'friendly_file_name', label: 'Friendly Name' },
                  { key: 'file_name', label: 'File Name' },
                  { key: 'size', label: 'Size' },
                  { key: 'updated_at', label: 'Updated At' },
                  { key: 'updated_by', label: 'Updated By' },
                  ...Object.keys(customData.categoryTagList).map(
                    (category) => ({
                      key: category,
                      label: category,
                    }),
                  ),
                  { key: 'actions', label: 'Actions', sortable: false },
                ].map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-2 text-left text-sm font-semibold text-gray-500 select-none"
                  >
                    {'sortable' in col && col.sortable === false ? (
                      col.label
                    ) : (
                      <button
                        type="button"
                        className="flex items-center gap-1 group bg-transparent border-0 p-0 m-0 text-inherit"
                        onClick={() => handleSort(col.key)}
                        aria-label={`Sort by ${col.label}`}
                      >
                        <span>{col.label}</span>
                        {sortConfig?.key === col.key ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowDropUpIcon
                              className="text-gray-400 group-hover:text-gray-700"
                              fontSize="small"
                            />
                          ) : (
                            <ArrowDropDownIcon
                              className="text-gray-400 group-hover:text-gray-700"
                              fontSize="small"
                            />
                          )
                        ) : (
                          <ArrowDropDownIcon
                            className="opacity-0 group-hover:opacity-60"
                            fontSize="small"
                          />
                        )}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map((file) => (
                <tr
                  key={file.id}
                  className="border-b last:border-b-0 hover:bg-gray-50 even:bg-gray-50 text-sm"
                >
                  <td className="px-4 py-2">{file.friendly_file_name || ''}</td>
                  <td className="px-4 py-2">
                    <a
                      href={`/secure-file?fileId=${encodeURIComponent(file.file_name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {file.file_name}
                    </a>
                  </td>
                  <td className="px-4 py-2">
                    {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                  </td>
                  <td className="px-4 py-2">
                    {file.updated_at
                      ? new Date(file.updated_at).toLocaleString()
                      : ''}
                  </td>
                  <td className="px-4 py-2">{file.updated_by || ''}</td>
                  {Object.keys(customData.categoryTagList).map((category) => (
                    <td key={category} className="px-4 py-2">
                      {file.tags && file.tags[category]
                        ? file.tags[category].join(', ')
                        : ''}
                    </td>
                  ))}
                  <td className="px-4 py-2 flex gap-2">
                    <button
                      type="button"
                      title="Copy download link"
                      className="p-1 rounded hover:bg-gray-200"
                      onClick={() =>
                        handleCopyFile(
                          `/secure-file?fileId=${encodeURIComponent(file.file_name)}`,
                        )
                      }
                    >
                      <ContentCopyIcon fontSize="small" />
                    </button>
                    <button
                      type="button"
                      title="Delete file"
                      className="p-1 rounded hover:bg-red-100"
                      onClick={() => {
                        setConfirmDeleteOpen(true);
                        setFileToDelete(encodeURIComponent(file.file_name));
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                    <button
                      type="button"
                      title="Edit file"
                      className="p-1 rounded hover:bg-blue-100"
                      onClick={() => {
                        setIsSecureFileFormOpen(true);
                        setFileToEdit(file);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        confirmMessage={`Are you sure you want to delete this file? (${decodeURIComponent(fileToDelete || '')})`}
        handleClose={() => setConfirmDeleteOpen(false)}
        handleYes={() => {
          handleDeleteFile(fileToDelete || '');
          setConfirmDeleteOpen(false);
        }}
        noButtonColor={theme.palette.primary.main}
      />
      <SecureFileForm
        categoryTagList={customData.categoryTagList}
        categoryTagDef={customData.categoryTagDef}
        isOpen={isSecureFileFormOpen}
        onClose={() => {
          setFileToEdit(null);
          setIsSecureFileFormOpen(false);
        }}
        isSubmitting={uploadingFile}
        onSubmit={(data) => {
          handleAddFile(data);
        }}
        editFile={fileToEdit}
        mode={fileToEdit ? 'edit' : 'add'}
      />
    </React.Fragment>
  );
};

export default AdminSecureFileList;

// This is called during server-side rendering (not part of NextJS).
// @ts-ignore: Agility specific
AdminSecureFileList.getCustomInitialProps = async ({
  agility,
  _channelName,
  languageCode,
}) => {
  const api = agility;

  const categoryTagList: Map<string, string[]> = new Map();
  const categoryTagDef: Map<string, boolean> = new Map();
  try {
    let raw = await api.getContentList({
      referenceName: 'categorytaglist',
      languageCode,
      contentLinkDepth: 2,
      expandAllContentLinks: true,
      depth: 2,
      filter: 'fields.listName[eq]"SubscriberResources"',
      take: 50,
    });

    raw?.items.forEach((item) => {
      const category = item.fields.category.fields.category;
      const categoryIsRequired =
        item.fields.category.fields.isRequired === 'true';
      const tagName = item.fields.tag.fields.tag;
      const tagValues = categoryTagList.get(category) || [];
      tagValues.push(tagName);
      categoryTagList.set(category, tagValues);
      categoryTagDef.set(category, categoryIsRequired);
    });
    // sort by key (category)
    const sortedEntries = Array.from(categoryTagList.entries()).sort(
      ([a], [b]) => a.localeCompare(b),
    );
    categoryTagList.clear();
    for (const [category, tags] of sortedEntries) {
      categoryTagList.set(category, tags);
    }
    return {
      categoryTagList: Object.fromEntries(categoryTagList),
      categoryTagDef: Object.fromEntries(categoryTagDef),
    };
  } catch (error) {
    await logServerException(error);
    if (console) console.error(error);
  }
};
